import {isLabelValue, isLabelValueEmpty} from './checkObjects';
import {getWayOfBuyingLabelValue} from './wayOfBuying';

export const HEADER_PROPERTY_PRIORITY = {
    projectTitle: ['excel'],
    vendorCode: ['psl', 'excel'], // If PSL vendor starts with 9, we take the Excel one
    vendorName: ['psl', 'excel'],
    wayOfBuying: ['psl', 'excel'],
    spender: ['excel', 'userDefaults'],
    requester: ['excel', 'userDefaults'],
    companyCode: ['psl', 'excel', 'userDefaults'],
    purchaseOrganization: ['excel', 'userDefaults'],
    purchasingGroup: ['excel', 'userDefaults'],
    validityStart: ['excel'],
    validityEnd: ['excel'],
    alternativePTs: ['excel'],
    incoterms: ['excel', 'masterData'],
    incoterms2: ['excel', 'masterData'],
    orderingAddress: ['masterData', 'excel'],
    invoicePartner: ['masterData', 'excel'],
    goodReceipt: ['excel', 'masterData'],
    grBasedInvoice: ['excel', 'masterData'],
    ers: ['excel', 'masterData'],
    internalComment: ['excel'],
    externalComment: ['excel'],
    sendPOToRequester: ['excel', 'default'],
    sendPOToSpender: ['excel', 'default'],
    sendPOToOther: ['excel', 'default'],
    othersAddress: ['excel', 'default'],
    sendPOToSupplier: ['excel', 'default'],
    supplierAddress: ['excel', 'default'],
    hidePrice: ['excel', 'userDefaults'],
};

export const ITEMS_PROPERTY_PRIORITY = {
    pslMaterialId: ['excel'],
    materialNumber: ['psl', 'excel'],
    description: ['excel', 'psl', 'masterData'],
    quantity: ['excel'],
    unitOfMeasure: ['excel', 'psl'],
    priceUnit: ['excel', 'psl'],
    pricePer: ['excel', 'psl'],
    currency: ['excel', 'psl', 'userDefaults'],
    freeItem: ['excel'],
    taxCode: ['excel', 'userDefaults'],
    requestedDeliveryDate: ['excel', 'psl', 'default'],
    plant: ['psl', 'excel', 'userDefaults'],
    storageLocation: ['excel', 'userDefaults'],
    deliveryAddressName: ['excel', 'userDefaults'],
    deliveryAddressName2: ['excel', 'userDefaults'],
    deliveryAddressHouseNumber: ['excel', 'userDefaults'],
    deliveryAddressStreet: ['excel', 'userDefaults'],
    deliveryAddressPostalCode: ['excel', 'userDefaults'],
    deliveryAddressCity: ['excel', 'userDefaults'],
    deliveryAddressState: ['excel', 'userDefaults'],
    deliveryAddressCountry: ['excel', 'userDefaults'],
    vendorMaterialNumber: ['excel', 'psl'],
    internalComment: ['excel'],
    externalComment: ['excel'],
    wbs: ['excel', 'userDefaults'],
    costCenter: ['excel', 'userDefaults'],
    profitCenter: ['excel', 'userDefaults'],
    glNumber: ['excel', 'userDefaults'],
    allocationType: ['excel'],
    allocatedFor: ['excel'],
};

/**
 * Gets the property value based on context priority.
 * @param {string} propertyName
 * @param {object} dataSources
 * @param {object} options
 * @param {string} context
 * @return {any}
 */
export const getPropertyValue = (propertyName, dataSources, options = {}, context = 'header') => {
    let priorities;
    if (context === 'header') {
        priorities = HEADER_PROPERTY_PRIORITY[propertyName];
    } else if (context === 'items') {
        priorities = ITEMS_PROPERTY_PRIORITY[propertyName];
    } else {
        throw new Error(`Invalid context "${context}" provided to getPropertyValue.`);
    }

    if (!priorities) {
        throw new Error(`No priority defined for property: ${propertyName} in context: ${context}`);
    }

    for (const source of priorities) {
        const value = dataSources[source]?.[propertyName];

        if (value !== undefined && value !== null) {
            if (typeof value === 'object') {
                if (value.hasOwnProperty('data') && value.hasOwnProperty('manuallyModified')) {
                    const data = value.data;
                    if (data && data.label !== '' && data.value !== '') {
                        if (options.validator && !options.validator(value, source)) {
                            continue;
                        }
                        return value;
                    }
                } else if (value.hasOwnProperty('label') && value.hasOwnProperty('value')) {
                    if (value.label !== '' && value.value !== '') {
                        if (options.validator && !options.validator(value, source)) {
                            continue;
                        }
                        return value;
                    }
                } else if (Object.keys(value).length > 0) {
                    if (options.validator && !options.validator(value, source)) {
                        continue;
                    }
                    return value;
                }
            } else if (value !== '') {
                if (options.validator && !options.validator(value, source)) {
                    continue;
                }
                return value;
            }
        }
    }

    // Return default value if provided
    if (options.hasOwnProperty('defaultValue')) {
        return options.defaultValue;
    }

    // If no value found, return null
    return null;
};

export const vendorCodeValidator = (value, source) => {
    return !(source === 'psl' && value.startsWith('9'));
};

/**
 * Gets a user from users list.
 * @param {string} userID
 * @param {array} users
 * @return {any}
 */
export const findUser = (userID, users) => {
    if (!userID) {
        return null;
    }

    const user = users.find(
        (u) =>
            (u.id && u.id.toLowerCase() === userID.toLowerCase()) ||
            (u.description && u.description.toLowerCase() === userID.toLowerCase()),
    );

    if (!user) {
        console.warn(`User ID "${userID}" not found in list.`);
        return null;
    }

    return {
        label: user.description || userID,
        value: user.id || userID,
        email: user.email || '',
    };
};

/**
 * Validates that a given value is a non-empty LabelValue object.
 * @param {LabelValue} value
 * @return {boolean}
 */
export const labelValueValidator = (value) =>
    isLabelValue(value) && !isLabelValueEmpty(value);


/**
 * Determines Way Of Buying based on PSL result and Excel.
 * @param {object} dataSources
 * @return {any}
 */
export const determineWayOfBuying = (dataSources) => {
    if (dataSources.psl && dataSources.psl.wayOfBuyingArray && dataSources.psl.wayOfBuyingArray.length > 0) {
        const pslWayOfBuyingArray = dataSources.psl.wayOfBuyingArray;

        const pslWoBLabelValues = pslWayOfBuyingArray.map((wobCode) => getWayOfBuyingLabelValue(wobCode)).filter(Boolean);

        if (dataSources.excel && dataSources.excel.wayOfBuying) {
            const excelWoBLabelValue = dataSources.excel.wayOfBuying;

            const match = pslWoBLabelValues.find((wobLabelValue) => wobLabelValue.value === excelWoBLabelValue.value);
            if (match) {
                return match;
            } else {
                return pslWoBLabelValues[0];
            }
        } else {
            return pslWoBLabelValues[0];
        }
    } else if (dataSources.excel && dataSources.excel.wayOfBuying) {
        return dataSources.excel.wayOfBuying;
    } else {
        return null;
    }
};
